import { TranslateFeature } from '../estate/estate-features';

export type TrObjectType = 'news' | 'defect' | 'residentIssue' | 'concierge';
export type TrMessageType = 'defectNote' | 'chatMessage' | 'conciergeMessage';
export type TrKey = TrObjectType | TrMessageType;

export type Localised<T> = { [code in Language]?: T };

export interface LocalisedType {
  id: string;
  label: Localised<string>;
}

// https://cloud.google.com/translate/docs/languages
// prettier-ignore
export const LANG_CODES = [
  'ab',   'ace',  'ach',  'af',   'sq',   'alz',  'am',   'ar',   'hy',   'as',   'awa',  'ay',   'az',   'ban',  'bm',   'ba',
  'eu',   'btx',  'bts',  'bbc',  'be',   'bem',  'bn',   'bew',  'bho',  'bik',  'bs',   'br',   'bg',   'bua',  'yue',  'ca',
  'ceb',  'ny',   'zh',   'zh-CN', 'Zh-TW',       'cv',   'co',   'crh',  'hr',   'cs',   'da',   'din',  'dv',   'doi',  'dov',
  'nl',   'dz',   'en',   'eo',   'et',   'ee',   'fj',   'fil',  'tl',   'fi',   'fr',   'fr-FR', 'fr-CA',       'fy',   'ff',
  'gaa',  'gl',   'lg',   'ka',   'de',   'el',   'gn',   'gu',   'ht',   'cnh',  'ha',   'haw',  'iw',   'he',   'hil',  'hi',
  'hmn',  'hu',   'hrx',  'is',   'ig',   'ilo',  'id',   'ga',   'it',   'ja',   'jw',   'jv',   'kn',   'pam',  'kk',   'km',
  'cgg',  'rw',   'ktu',  'gom',  'ko',   'kri',  'ku',   'ckb',  'ky',   'lo',   'ltg',  'la',   'lv',   'lij',  'li',   'ln',
  'lt',   'lmo',  'luo',  'lb',   'mk',   'mai',  'mak',  'mg',   'ms',   'ms-Arab',      'ml',   'mt',   'mi',   'mr',   'chm',
  'mni-Mtei',     'min',  'lus',  'mn',   'my',   'nr',   'new',  'ne',   'nso',  'no',   'nus',  'oc',   'or',   'om',   'pag',
  'pap',  'ps',   'fa',   'pl',   'pt',   'pt-PT', 'pt-BR',       'pa',   'pa-Arab',      'qu',   'rom',  'ro',   'rn',   'ru',
  'sm',   'sg',   'sa',   'gd',   'sr',   'st',   'crs',  'shn',  'sn',   'scn',  'szl',  'sd',   'si',   'sk',   'sl',   'so',
  'es',   'su',   'sw',   'ss',   'sv',   'tg',   'ta',   'tt',   'te',   'tet',  'th',   'ti',   'ts',   'tn',   'tr',   'tk',
  'ak',   'uk',   'ur',   'ug',   'uz',   'vi',   'cy',   'xh',   'yi',   'yo',   'yua',  'zu'
] as const;

export type Language = (typeof LANG_CODES)[number];

export namespace TrKey {
  /**
   * Maps TrKey to valid key of TranslateFeature.features
   * @param {TrKey} key
   * @return {TranslateFeature.features}
   */
  export function toFeatureFlag(key: TrKey): keyof NonNullable<TranslateFeature['features']> {
    switch (key) {
      case 'chatMessage':
        return 'chat';
      case 'defectNote':
        return 'defectConversations';
      case 'conciergeMessage':
        return 'conciergeConversations';
      default:
        return key;
    }
  }
}

/**
 * Return language name from code
 * @param {Language} code
 * @return {string}
 */
export function LangHr(code: Language): string {
  switch (code) {
    case 'ab':
      return 'Abkhaz';
    case 'ace':
      return 'Acehnese';
    case 'ach':
      return 'Acholi';
    case 'af':
      return 'Afrikaans';
    case 'sq':
      return 'Albanian';
    case 'alz':
      return 'Alur';
    case 'am':
      return 'Amharic';
    case 'ar':
      return 'Arabic';
    case 'hy':
      return 'Armenian';
    case 'as':
      return 'Assamese';
    case 'awa':
      return 'Awadhi';
    case 'ay':
      return 'Aymara';
    case 'az':
      return 'Azerbaijani';
    case 'ban':
      return 'Balinese';
    case 'bm':
      return 'Bambara';
    case 'ba':
      return 'Bashkir';
    case 'eu':
      return 'Basque';
    case 'btx':
      return 'Batak Karo';
    case 'bts':
      return 'Batak Simalungun';
    case 'bbc':
      return 'Batak Toba';
    case 'be':
      return 'Belarusian';
    case 'bem':
      return 'Bemba';
    case 'bn':
      return 'Bengali';
    case 'bew':
      return 'Betawi';
    case 'bho':
      return 'Bhojpuri';
    case 'bik':
      return 'Bikol';
    case 'bs':
      return 'Bosnian';
    case 'br':
      return 'Breton';
    case 'bg':
      return 'Bulgarian';
    case 'bua':
      return 'Buryat';
    case 'yue':
      return 'Cantonese';
    case 'ca':
      return 'Catalan';
    case 'ceb':
      return 'Cebuano';
    case 'ny':
      return 'Chichewa (Nyanja)';
    case 'zh':
    case 'zh-CN':
      return 'Chinese (Simplified)';
    case 'Zh-TW':
      return 'Chinese (Traditional)';
    case 'cv':
      return 'Chuvash';
    case 'co':
      return 'Corsican';
    case 'crh':
      return 'Crimean Tatar';
    case 'hr':
      return 'Croatian';
    case 'cs':
      return 'Czech';
    case 'da':
      return 'Danish';
    case 'din':
      return 'Dinka';
    case 'dv':
      return 'Divehi';
    case 'doi':
      return 'Dogri';
    case 'dov':
      return 'Dombe';
    case 'nl':
      return 'Dutch';
    case 'dz':
      return 'Dzongkha';
    case 'en':
      return 'English';
    case 'eo':
      return 'Esperanto';
    case 'et':
      return 'Estonian';
    case 'ee':
      return 'Ewe';
    case 'fj':
      return 'Fijian';
    case 'fil':
    case 'tl':
      return 'Filipino (Tagalog)';
    case 'fi':
      return 'Finnish';
    case 'fr':
      return 'French';
    case 'fr-FR':
      return 'French (French)';
    case 'fr-CA':
      return 'French (Canadian)';
    case 'fy':
      return 'Frisian';
    case 'ff':
      return 'Fulfulde';
    case 'gaa':
      return 'Ga';
    case 'gl':
      return 'Galician';
    case 'lg':
      return 'Ganda (Luganda)';
    case 'ka':
      return 'Georgian';
    case 'de':
      return 'German';
    case 'el':
      return 'Greek';
    case 'gn':
      return 'Guarani';
    case 'gu':
      return 'Gujarati';
    case 'ht':
      return 'Haitian Creole';
    case 'cnh':
      return 'Hakha Chin';
    case 'ha':
      return 'Hausa';
    case 'haw':
      return 'Hawaiian';
    case 'iw':
    case 'he':
      return 'Hebrew';
    case 'hil':
      return 'Hiligaynon';
    case 'hi':
      return 'Hindi';
    case 'hmn':
      return 'Hmong';
    case 'hu':
      return 'Hungarian';
    case 'hrx':
      return 'Hunsrik';
    case 'is':
      return 'Icelandic';
    case 'ig':
      return 'Igbo';
    case 'ilo':
      return 'Iloko';
    case 'id':
      return 'Indonesian';
    case 'ga':
      return 'Irish';
    case 'it':
      return 'Italian';
    case 'ja':
      return 'Japanese';
    case 'jw':
    case 'jv':
      return 'Javanese';
    case 'kn':
      return 'Kannada';
    case 'pam':
      return 'Kapampangan';
    case 'kk':
      return 'Kazakh';
    case 'km':
      return 'Khmer';
    case 'cgg':
      return 'Kiga';
    case 'rw':
      return 'Kinyarwanda';
    case 'ktu':
      return 'Kituba';
    case 'gom':
      return 'Konkani';
    case 'ko':
      return 'Korean';
    case 'kri':
      return 'Krio';
    case 'ku':
      return 'Kurdish (Kurmanji)';
    case 'ckb':
      return 'Kurdish (Sorani)';
    case 'ky':
      return 'Kyrgyz';
    case 'lo':
      return 'Lao';
    case 'ltg':
      return 'Latgalian';
    case 'la':
      return 'Latin';
    case 'lv':
      return 'Latvian';
    case 'lij':
      return 'Ligurian';
    case 'li':
      return 'Limburgan';
    case 'ln':
      return 'Lingala';
    case 'lt':
      return 'Lithuanian';
    case 'lmo':
      return 'Lombard';
    case 'luo':
      return 'Luo';
    case 'lb':
      return 'Luxembourgish';
    case 'mk':
      return 'Macedonian';
    case 'mai':
      return 'Maithili';
    case 'mak':
      return 'Makassar';
    case 'mg':
      return 'Malagasy';
    case 'ms':
      return 'Malay';
    case 'ms-Arab':
      return 'Malay (Jawi)';
    case 'ml':
      return 'Malayalam';
    case 'mt':
      return 'Maltese';
    case 'mi':
      return 'Maori';
    case 'mr':
      return 'Marathi';
    case 'chm':
      return 'Meadow Mari';
    case 'mni-Mtei':
      return 'Meiteilon (Manipuri)';
    case 'min':
      return 'Minang';
    case 'lus':
      return 'Mizo';
    case 'mn':
      return 'Mongolian';
    case 'my':
      return 'Myanmar (Burmese)';
    case 'nr':
      return 'Ndebele (South)';
    case 'new':
      return 'Nepalbhasa (Newari)';
    case 'ne':
      return 'Nepali';
    case 'nso':
      return 'Northern Sotho (Sepedi)';
    case 'no':
      return 'Norwegian';
    case 'nus':
      return 'Nuer';
    case 'oc':
      return 'Occitan';
    case 'or':
      return 'Odia (Oriya)';
    case 'om':
      return 'Oromo';
    case 'pag':
      return 'Pangasinan';
    case 'pap':
      return 'Papiamento';
    case 'ps':
      return 'Pashto';
    case 'fa':
      return 'Persian';
    case 'pl':
      return 'Polish';
    case 'pt':
      return 'Portuguese';
    case 'pt-PT':
      return 'Portuguese (Portugal)';
    case 'pt-BR':
      return 'Portuguese (Brazil)';
    case 'pa':
      return 'Punjabi';
    case 'pa-Arab':
      return 'Punjabi (Shahmukhi)';
    case 'qu':
      return 'Quechua';
    case 'rom':
      return 'Romani';
    case 'ro':
      return 'Romanian';
    case 'rn':
      return 'Rundi';
    case 'ru':
      return 'Russian';
    case 'sm':
      return 'Samoan';
    case 'sg':
      return 'Sango';
    case 'sa':
      return 'Sanskrit';
    case 'gd':
      return 'Scots Gaelic';
    case 'sr':
      return 'Serbian';
    case 'st':
      return 'Sesotho';
    case 'crs':
      return 'Seychellois Creole';
    case 'shn':
      return 'Shan';
    case 'sn':
      return 'Shona';
    case 'scn':
      return 'Sicilian';
    case 'szl':
      return 'Silesian';
    case 'sd':
      return 'Sindhi';
    case 'si':
      return 'Sinhala (Sinhalese)';
    case 'sk':
      return 'Slovak';
    case 'sl':
      return 'Slovenian';
    case 'so':
      return 'Somali';
    case 'es':
      return 'Spanish';
    case 'su':
      return 'Sundanese';
    case 'sw':
      return 'Swahili';
    case 'ss':
      return 'Swati';
    case 'sv':
      return 'Swedish';
    case 'tg':
      return 'Tajik';
    case 'ta':
      return 'Tamil';
    case 'tt':
      return 'Tatar';
    case 'te':
      return 'Telugu';
    case 'tet':
      return 'Tetum';
    case 'th':
      return 'Thai';
    case 'ti':
      return 'Tigrinya';
    case 'ts':
      return 'Tsonga';
    case 'tn':
      return 'Tswana';
    case 'tr':
      return 'Turkish';
    case 'tk':
      return 'Turkmen';
    case 'ak':
      return 'Twi (Akan)';
    case 'uk':
      return 'Ukrainian';
    case 'ur':
      return 'Urdu';
    case 'ug':
      return 'Uyghur';
    case 'uz':
      return 'Uzbek';
    case 'vi':
      return 'Vietnamese';
    case 'cy':
      return 'Welsh';
    case 'xh':
      return 'Xhosa';
    case 'yi':
      return 'Yiddish';
    case 'yo':
      return 'Yoruba';
    case 'yua':
      return 'Yucatec Maya';
    case 'zu':
      return 'Zulu';
  }
}
